<script lang="ts" setup>
import { onMounted, ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import sharedService from '../services/sharedService';
import { Status } from '../constants/enums';
import {
  BranchOption,
  BranchData,
  BranchChangeEvent,
  EmissionPoint,
  BranchResponse,
} from '@/common/models/branch-select.interface';

const { t } = useI18n();
const emit = defineEmits(['change', 'clearEmissionPoint']);

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  emissionPointId: {
    type: String,
    default: null,
  },
});

// Data
const lstBranches = ref<BranchData[]>([]);
const lstEmissionsPoints = ref<EmissionPoint[]>([]);
const selectedBranch = ref<BranchOption | null>(null);
const selectedEmissionPoint = ref<BranchOption | null>(null);
const selectReturnBranch = ref<BranchResponse | null>(null);

// Search filters
const branchSearchText = ref('');
const emissionPointSearchText = ref('');

// Dropdown refs
const branchDropdownRef = ref();
const emissionPointDropdownRef = ref();

// Computed options
const branchOptions = computed<BranchOption[]>(() => {
  return lstBranches.value.map((item) => ({
    name: item.branch.name,
    code: item.branch.establishmentCode?.toString().padStart(3, '0') || '',
    id: item.branch.id || 0,
  }));
});

const emissionPointOptions = computed<BranchOption[]>(() => {
  return lstEmissionsPoints.value.map((item) => ({
    name: item.name,
    code: item.pointCode.toString().padStart(3, '0'),
    id: item.id || 0,
  }));
});

// Filtered options based on search
const filteredBranchOptions = computed<BranchOption[]>(() => {
  if (!branchSearchText.value) return branchOptions.value;
  const search = branchSearchText.value.toLowerCase();
  return branchOptions.value.filter(
    (option) => option.name.toLowerCase().includes(search) || option.code.toLowerCase().includes(search),
  );
});

const filteredEmissionPointOptions = computed<BranchOption[]>(() => {
  if (!emissionPointSearchText.value) return emissionPointOptions.value;
  const search = emissionPointSearchText.value.toLowerCase();
  return emissionPointOptions.value.filter(
    (option) => option.name.toLowerCase().includes(search) || option.code.toLowerCase().includes(search),
  );
});

// Local visible state
const localVisible = computed({
  get: () => props.visible,
  set: (value) => {
    if (!value) {
      emit('change', null); // Emit null to close without changes
    }
  },
});

// Load branches
const loadBranches = async () => {
  try {
    const response = await sharedService.getBranches();
    lstBranches.value = (response as BranchData[]).filter((item) => item.branch.status === Status.Active);
  } catch (error) {
    console.error('Error loading branches:', error);
  }
};

// Input handlers - Single Responsibility Principle
const onBranchInput = (event: any) => {
  const inputValue = event.target?.value || '';
  branchSearchText.value = inputValue;

  // Auto-open dropdown for better UX
  if (branchDropdownRef.value && !branchDropdownRef.value.overlayVisible) {
    branchDropdownRef.value.show();
  }
};

const onEmissionPointInput = (event: any) => {
  const inputValue = event.target?.value || '';
  emissionPointSearchText.value = inputValue;

  // Auto-open dropdown for better UX
  if (emissionPointDropdownRef.value && !emissionPointDropdownRef.value.overlayVisible) {
    emissionPointDropdownRef.value.show();
  }
};

// Clear handlers - Single Responsibility Principle
const clearBranchSelection = () => {
  selectedBranch.value = null;
  branchSearchText.value = '';
  // Reset dependent emission point
  selectedEmissionPoint.value = null;
  emissionPointSearchText.value = '';
  lstEmissionsPoints.value = [];
  selectReturnBranch.value = null;
};

const clearEmissionPointSelection = () => {
  selectedEmissionPoint.value = null;
  emissionPointSearchText.value = '';
};

// Click handlers - Open dropdown when clicking anywhere on input
const onBranchClick = () => {
  if (branchDropdownRef.value && !branchDropdownRef.value.overlayVisible) {
    branchDropdownRef.value.show();
  }
};

const onEmissionPointClick = () => {
  if (!selectedBranch.value) {
    return; // Don't open if branch is not selected
  }
  if (emissionPointDropdownRef.value && !emissionPointDropdownRef.value.overlayVisible) {
    emissionPointDropdownRef.value.show();
  }
};

// Handle branch change - Open/Closed Principle
const handleBranchChange = () => {
  // Reset emission point state
  resetEmissionPointState();

  if (!selectedBranch.value) {
    selectReturnBranch.value = null;
    return;
  }

  // Update branch-related data
  updateBranchRelatedData();
};

// Helper functions following Single Responsibility Principle
const resetEmissionPointState = () => {
  selectedEmissionPoint.value = null;
  lstEmissionsPoints.value = [];
  emissionPointSearchText.value = '';
};

const updateBranchRelatedData = () => {
  const branchData = lstBranches.value.find((item) => item.branch.id === selectedBranch.value?.id);
  if (!branchData) return;

  selectReturnBranch.value = branchData.branch;
  lstEmissionsPoints.value = branchData.emissionPointsLst.filter((item) => item.status === Status.Active);
};

// Handle emission point change and close modal
const handleEmissionPointChange = () => {
  if (!selectedEmissionPoint.value || !selectReturnBranch.value) return;

  const emissionPoint = lstEmissionsPoints.value.find((item) => item.id === selectedEmissionPoint.value?.id);
  if (!emissionPoint) return;

  const changeEvent: BranchChangeEvent = {
    branch: selectReturnBranch.value,
    emissionPoint: emissionPoint,
  };

  emit('change', changeEvent);
};

// Initialize selection with emission point
const initializeSelection = (emissionPointId: string | number) => {
  const branchData = lstBranches.value.find((item) =>
    item.emissionPointsLst.some((ep) => ep.id?.toString() === emissionPointId.toString()),
  );

  if (!branchData) return;

  const emissionPointData = branchData.emissionPointsLst.find((ep) => ep.id?.toString() === emissionPointId.toString());

  if (!emissionPointData) return;

  selectedBranch.value = branchOptions.value.find((option) => option.id === branchData.branch.id) || null;
  selectReturnBranch.value = branchData.branch;
  lstEmissionsPoints.value = branchData.emissionPointsLst.filter((item) => item.status === Status.Active);
  selectedEmissionPoint.value = emissionPointOptions.value.find((option) => option.id === emissionPointData.id) || null;
};

// Initialize with first branch only (no emission point selected)
const initializeWithFirstBranch = () => {
  if (lstBranches.value.length === 0) return;

  const firstBranch = lstBranches.value[0];
  selectedBranch.value = branchOptions.value.find((option) => option.id === firstBranch.branch.id) || null;
  selectReturnBranch.value = firstBranch.branch;
  lstEmissionsPoints.value = firstBranch.emissionPointsLst.filter((item) => item.status === Status.Active);
  selectedEmissionPoint.value = null; // No emission point selected by default
};

// Clear emission point
const clearEmissionPoint = () => {
  emit('clearEmissionPoint', props.emissionPointId);
  localVisible.value = false;
};

// Close modal handler
const closeModal = () => {
  localVisible.value = false;
};

// Watchers
watch(
  () => props.visible,
  async (newValue) => {
    if (newValue) {
      // Reset search filters when opening modal
      branchSearchText.value = '';
      emissionPointSearchText.value = '';

      await loadBranches();
      if (props.emissionPointId) {
        initializeSelection(props.emissionPointId);
      } else {
        // If no emission point is set, select first branch but no emission point
        initializeWithFirstBranch();
      }
    } else {
      // Clear search filters when closing modal
      branchSearchText.value = '';
      emissionPointSearchText.value = '';
    }
  },
);

watch(
  () => props.emissionPointId,
  (newValue) => {
    if (newValue && lstBranches.value.length > 0) {
      initializeSelection(newValue);
    }
  },
);

// Load on mount
onMounted(() => {
  loadBranches();
});
</script>

<template>
  <Dialog
    v-model:visible="localVisible"
    :header="$t('shared.emissionPoint')"
    :modal="true"
    :closable="true"
    :dismissableMask="false"
    :draggable="false"
    appendTo="self"
    position="topright"
    :style="{ width: '450px' }"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }">
    <div class="p-fluid">
      <!-- Branch Selector -->
      <div class="field mb-4">
        <label for="selectBranch" class="font-semibold mb-2 block">{{ $t('shared.branch') }}</label>

        <Dropdown
          ref="branchDropdownRef"
          id="selectBranch"
          v-model="selectedBranch"
          :options="filteredBranchOptions"
          optionLabel="name"
          :placeholder="$t('branches.selectBranch')"
          :editable="true"
          :showClear="true"
          @change="handleBranchChange"
          @input="onBranchInput"
          @clear="clearBranchSelection"
          @click="onBranchClick"
          class="w-full"
          :emptyMessage="$t('shared.noResults')">
          <template #value="slotProps">
            <span v-if="slotProps.value">{{ slotProps.value.code }} - {{ slotProps.value.name }}</span>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
          <template #option="slotProps"> {{ slotProps.option.code }} - {{ slotProps.option.name }} </template>
        </Dropdown>
      </div>

      <!-- Emission Point Selector -->
      <div class="field mb-4">
        <label for="selectEmissionPoint" class="font-semibold mb-2 block">{{ $t('shared.emissionPoint') }}</label>

        <Dropdown
          ref="emissionPointDropdownRef"
          id="selectEmissionPoint"
          v-model="selectedEmissionPoint"
          :options="filteredEmissionPointOptions"
          optionLabel="name"
          :placeholder="$t('branches.selectEmissionPoint')"
          :editable="true"
          :disabled="!selectedBranch"
          :showClear="true"
          @change="handleEmissionPointChange"
          @input="onEmissionPointInput"
          @clear="clearEmissionPointSelection"
          @click="onEmissionPointClick"
          class="w-full"
          :emptyMessage="$t('shared.noResults')">
          <template #value="slotProps">
            <span v-if="slotProps.value">{{ slotProps.value.code }} - {{ slotProps.value.name }}</span>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
          <template #option="slotProps"> {{ slotProps.option.code }} - {{ slotProps.option.name }} </template>
        </Dropdown>
      </div>
    </div>

    <!-- Dialog Footer with consistent button pattern -->
    <div class="p-dialog-footer">
      <Button
        v-if="props.emissionPointId"
        severity="warn"
        :text="true"
        :label="$t('branches.clearEmissionPointAssignment')"
        icon="pi pi-times-circle"
        @click="clearEmissionPoint"
        v-tooltip.bottom="$t('branches.clearEmissionPointAssignmentInfo2')" />
      <Button
        severity="danger"
        :text="true"
        :label="$t('shared.close')"
        icon="pi pi-times"
        @click="closeModal"
        v-tooltip.bottom="$t('shared.close')" />
    </div>
  </Dialog>
</template>

<style scoped>
.font-semibold {
  font-weight: 600;
}
</style>
